import { useMemo, useState } from "react";
import { groupBy } from "lodash";
import * as ss from "simple-statistics";
import { api, useApi } from "../../services/api";
import { Loading } from "../components";
import { TableStrategy, TableStrategyOutput } from "../components/Table";
import Chart from "../components/Chart";
import { addDay, dayForDateTime, formatDay, getDateTime } from "../../services/date";
import { capitalStart, getGain, getGainPrice, getResultsClosed } from "../../services/common";

import { Dataset, Line, StrategyDB, StrategyDetail, StrategyOutput } from "../../../types";

export default function Strategy() {

    const [strategies, error, isLoading, status, reload] = useApi.getStrategies({});

    const [strategy, setStrategy] = useState<null | StrategyDetail>(null);
    const [isRunning, setIsRunning] = useState(false);

    function handleOnChange(result: StrategyDetail) {
        reload();
        setStrategy(result);
    }

    async function handleRun(row: StrategyDB) {
        setIsRunning(true);
        setStrategy(null);
        const [result, error] = await api.getStrategy({ strategy: row.strategy, dataset: row.dataset });

        if (error) throw new Error(error);
        if (result) handleOnChange(result);

        setIsRunning(false);
    }

    return <div>
        <h1>Strategie</h1>

        <StrategyList onChange={handleOnChange} />

        <h2>Tabulka strategií</h2>
        {error && <p className="error">Chyba: {error}</p>}
        {isLoading && <Loading />}
        {strategies && <>
            <TableStrategy data={strategies} onClick={handleRun} />

            {isRunning && <div className="loading">Strategie běží ...</div>}

            {!strategy && <p className="info">Klikni do tabulky nebo spusť strategii.</p>}
            {strategy && <>
                <StrategyChart {...strategy} />
            </>}
        </>}
    </div>;
}

function StrategyList({ onChange }: { onChange: (result: StrategyDetail) => void }) {

    const [strategyList, error, isLoading] = useApi.getStrategyList({});

    const [isRunning, setIsRunning] = useState(false);
    const [strategy, setStrategy] = useState("");
    const [dataset, setDataset] = useState<Dataset>("tech");

    async function handleRun() {
        if (!strategy) return;

        setIsRunning(true);

        const [result, error] = await api.getStrategy({ strategy, dataset });

        if (error) throw new Error(error);
        if (result) onChange(result);

        setIsRunning(false);
    }

    return <>
        <h2>Strategie</h2>
        {error && <p className="error">Chyba: {error}</p>}
        {isLoading && <Loading />}
        {strategyList && <>
            <select value={strategy} onChange={(e) => setStrategy(e.target.value)}>
                {strategyList.map((strategy) => <option key={strategy} value={strategy}>{strategy}</option>)}
            </select>
            <select value={dataset} onChange={(e) => setDataset(e.target.value as Dataset)}>
                <option value="tech">Vybraná data / technologické společnosti</option>
                <option value="alpaca">Alpaca</option>
                <option value="poly">Poly a XTB</option>
                <option value="us500">TODO: US500</option>
            </select>
            {!strategy && <p className="info">Vyber strategii</p>}
            {strategy && <>
                {isRunning && <div className="loading">Strategie běží</div>}
                {!isRunning && <button onClick={handleRun}>Spustit strategii</button>}
            </>}
        </>}
    </>;
}

function StrategyChart({ output, dateFrom, dateTo, ...strategy }: StrategyDetail) {

    const lines = useMemo(() => {
        const lines: Line[] = [];


        const resultsClosed = getResultsClosed(output);
        const groupedOutput = groupBy(resultsClosed, r => formatDay(r.dateClose));

        let capital = capitalStart;
        for (let date = dateFrom; date <= dateTo; date = date + dayForDateTime) {
            const day = formatDay(date);
            const results = groupedOutput[day];
            if (results) {
                const prices = results.map(getGainPrice);
                const todayPrice = ss.sum(prices);
                lines.push({ x1: date - dayForDateTime, x2: date, y1: capital, y2: capital + todayPrice, label: `Obchodů: ${prices.length}, Zisk: ${todayPrice.toFixed(2)} USD` });
                capital += todayPrice;
            } else {
                lines.push({ x1: date - dayForDateTime, x2: date, y1: capital, y2: capital });
            }
        }

        return lines;
    }, [output, dateFrom, dateTo]);

    const [select, setSelect] = useState<[number, number] | null>(null);

    const selectedData = useMemo(() => {
        if (!select) return [];

        return output
            .filter(({ date }) => date >= select[0] && date <= select[1]);
    }, [output, select]);

    const [selectedOutput, setSelectedOutput] = useState<null | StrategyOutput>(null);

    return <>
        <h2>Graf obchodů</h2>
        <TableStrategy data={[strategy]} />

        <Chart height={500} lines={lines} onSelect={setSelect} />

        <h3>Tabulka z výběru obchodů</h3>
        <p>Je označených <b>{selectedData.length}</b> obchodů.</p>
        <TableStrategyOutput
            data={selectedData.length ? selectedData : output}
            onClick={row => setSelectedOutput(row)}
        />

        <h2>Detail obchodu strategie</h2>
        {!selectedOutput && <p>Klikni do tabulky</p>}
        {selectedOutput && <StrategyOutputChart output={selectedOutput} outputs={output} dataset={strategy.dataset} />}
    </>
}


function StrategyOutputChart({ output, outputs, dataset }: { output: StrategyOutput, outputs: StrategyOutput[], dataset: Dataset }) {
    const symbol = output.symbol;

    const [stock, error, isLoading] = useApi.getData({ dataset, symbol, timeSpan: "minute" });

    const [lines, select] = useMemo(() => {
        let select: null | [number, number] = null;
        const lines: Line[] = [];

        lines.push({ x1: output.date, color: "white", width: 2, label: "Zadáno" });

        if (output.result.typeClose !== "not-open") {
            lines.push({ x1: output.result.dateOpen, color: "yellow", label: "Date Open" });
            lines.push({ x1: output.result.dateClose, color: "yellow", label: "Date Close" });

            lines.push({ y1: output.decision.openLow, color: "#888", label: "Threshold Open trade" });
            lines.push({ y1: output.decision.openHigh, color: "#888", label: "Threshold Close trade" });

            lines.push({ y1: output.result.open, color: "yellow", label: "Open trade" });
            lines.push({ y1: output.result.close, color: "yellow", label: "Close trade" });

            lines.push({ y1: output.decision.takeProfit, color: "green", width: 2, label: "Take profit" });
            lines.push({ y1: output.decision.stopLoss, color: "red", width: 2, label: "Stop loss" });

            lines.push(...output.decision.lines);

            select = [output.date - dayForDateTime, output.result.dateClose + dayForDateTime];
        }

        return [lines, select];
    }, [output]);

    const linesOutputs = useMemo<Line[]>(() => {
        if (!stock) return [];

        const groupedStock = groupBy(stock.date.map((date, i) => ({ date, avg: ss.average([stock.low[i], stock.high[i]]) })), row => formatDay(row.date));

        const groupedAverages: Record<string, { date: number, avg: number, price: number, gain: number }> = {};
        Object.entries(groupedStock).forEach(([day, rows]) => {
            groupedAverages[day] = {
                date: getDateTime(day),
                avg: ss.average(rows.map(row => row.avg)),
                price: 0,
                gain: 0,
            };
        });

        outputs.forEach(output => {
            if (output.symbol !== symbol) return;
            if (output.result.typeClose === "not-open") return;

            const day = formatDay(output.date);
            if (!groupedAverages[day]) throw new Error(`Day ${day} in stock not found but there is output`);
            groupedAverages[day].price = output.decision.price;
            groupedAverages[day].gain = getGain(output.result);
        });


        let x1: null | number = null;
        let y1: null | number = null;

        return Object
            .values(groupedAverages)
            .sort((a, b) => a.date - b.date)
            .map<Line>(({ date, avg, price, gain }) => {
                if (x1 === null)
                    x1 = addDay(date, -1);
                if (y1 === null)
                    y1 = avg;

                const line: Line = {
                    x1,
                    x2: date,
                    y1,
                    y2: avg,
                    color: gain > 0 ? "green" : gain < 0 ? "red" : "#FFFFFF33",
                    width: 3,
                    label: (price * gain).toFixed(2) + "USD",
                };

                x1 = date;
                y1 = avg;

                return line;
            });

    }, [outputs, stock]);

    return <>
        {error && <p className="error">Chyba: {error}</p>}
        {isLoading && <Loading />}
        {stock && <>
            <TableStrategyOutput data={[output]} />
            <Chart height={500} stock={stock} lines={lines} defaultSelect={select} />
            <h2>Detail obchodů na jednom artiklu</h2>
            <Chart height={500} lines={linesOutputs} defaultSelect={null} />
        </>}
    </>;
}